/**
 * The two states a company-scoped page shows when it has no company to show:
 * the account has no company yet, which is a step left to take, not an error;
 * or the company read failed, which is. `useCompanyPresence` tells them apart.
 */
import { EmptyState, ErrorState } from '@nt/ui/states'
import { Building2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import Link from '@/lib/navigation/link'
import { useGeneralStore } from '@/store/useGeneralStore'
import { OnboardingStatus } from '@/types/enums'
import { SUPPORT_EMAIL } from '@/utils/constants'

const SETUP_TITLE = 'Finish setting up your company'

/** A primary "Contact support" link that opens a mail to support. */
export function ContactSupportButton() {
  return (
    <Button nativeButton={false} render={<a href={`mailto:${SUPPORT_EMAIL}`} role="link" />}>
      Contact support
    </Button>
  )
}

/**
 * No company yet. A customer still in onboarding finishes it there. One who
 * finished onboarding without a company cannot: /onboarding sends a completed
 * customer straight back to /, and the app has no other way to start a company,
 * so support sets it up with them.
 */
export function CompanySetupEmptyState({
  description = 'This page fills in once your company is set up.',
}: {
  description?: string
}) {
  const onboardingStatus = useGeneralStore((state) => state.customerBootstrapCache?.onboardingStatus)

  if (onboardingStatus === OnboardingStatus.Completed) {
    return (
      <Card>
        <EmptyState
          className="min-h-[360px] justify-center"
          icon={<Building2 />}
          title="Your account has no company yet"
          description={
            <>
              {description} Write to <span className="font-medium text-foreground select-all">{SUPPORT_EMAIL}</span> and
              we will set it up with you.
            </>
          }
          action={<ContactSupportButton />}
        />
      </Card>
    )
  }

  return (
    <Card>
      <EmptyState
        className="min-h-[360px] justify-center"
        icon={<Building2 />}
        title={SETUP_TITLE}
        description={description}
        action={
          <Button nativeButton={false} render={<Link href="/onboarding" prefetch={false} role="link" />}>
            {SETUP_TITLE}
          </Button>
        }
      />
    </Card>
  )
}

/**
 * The company comes from the shell's bootstrap, so only a reload asks again. It
 * reloads the document itself rather than through the router, so pages mounted
 * without one (their tests) can render it.
 */
export function CompanyLoadErrorState({ onRetry = () => window.location.reload() }: { onRetry?: () => void }) {
  return (
    <Card>
      <ErrorState
        className="min-h-[360px] justify-center"
        title="We couldn't load your company"
        description="This page depends on your company's details, and they did not load."
        recovery={`Refresh the page to try again. If it keeps failing, write to ${SUPPORT_EMAIL}.`}
        retryLabel="Refresh the page"
        onRetry={onRetry}
      />
    </Card>
  )
}
